import { useLayoutEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { Alert, type AlertTone } from '@/components/atoms/Alert';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import type { CustomerBannerMessage, LocalizedString } from '@/domain/content';
import { parseLocale } from '@/utils/localeFormatters';
import { localStorageUtils } from '@/utils/localStorage';

import type { CustomerBannerMessageVariant } from './types';
import {
    getCustomerBannerMessageIcon,
    isCurrentPageInTargetedPage,
    makeLocalStorageKey,
} from './utils';

import styles from './CustomerBanner.module.scss';

interface CustomerBannerItem {
    id: string;
    variant: CustomerBannerMessageVariant;
    title: LocalizedString;
    content: LocalizedString;
    isVisible: boolean;
}

const TONE: Record<CustomerBannerMessageVariant, AlertTone> = {
    warning: 'warning',
    informative: 'info',
    neutral: 'neutral',
};

interface CustomerBannerProps {
    customerBannerMessages: CustomerBannerMessage[];
}

function CustomerBanner(props: CustomerBannerProps) {
    const { customerBannerMessages } = props;

    const [customerBannerItems, setCustomerBannerItems] = useState<CustomerBannerItem[]>([]);

    const currentPage = useLocation().pathname;
    const { i18n } = useTranslation();
    const currentLanguageKey = parseLocale(i18n.language);

    useLayoutEffect(() => {
        const customerBannerItems: CustomerBannerItem[] = customerBannerMessages?.map(
            (customerBannerMessage) => {
                const isBannerClosed = localStorageUtils.exists(
                    makeLocalStorageKey(customerBannerMessage.id)
                );
                const isShowOnAllPages = customerBannerMessage.showOnAllPages;
                const isInTargetedPage = customerBannerMessage.targeting.some((targetedPage) => {
                    return isCurrentPageInTargetedPage(currentPage, targetedPage);
                });

                const isVisible = !isBannerClosed && (isShowOnAllPages || isInTargetedPage);

                return {
                    id: customerBannerMessage.id,
                    variant: customerBannerMessage.variant,
                    title: customerBannerMessage.title,
                    content: customerBannerMessage.content,
                    isVisible: isVisible,
                };
            }
        );
        setCustomerBannerItems(customerBannerItems);
    }, [customerBannerMessages, currentPage]);

    const handleCloseBanner = (bannerId: string) => {
        localStorageUtils.set(makeLocalStorageKey(bannerId), true);

        setCustomerBannerItems((prev) =>
            prev.map((customerBannerItem) => {
                if (customerBannerItem.id === bannerId) {
                    return { ...customerBannerItem, isVisible: false };
                }
                return customerBannerItem;
            })
        );
    };

    const filteredCustomerBannerItems = useMemo(() => {
        return customerBannerItems.filter(
            (customerBannerItem) =>
                customerBannerItem.isVisible &&
                customerBannerItem.title[currentLanguageKey] &&
                customerBannerItem.content[currentLanguageKey]
        );
    }, [customerBannerItems, currentLanguageKey]);

    // If there are no visible customer banner items, return null
    if (filteredCustomerBannerItems.length === 0) {
        return null;
    }

    return (
        <div className={styles.customerBannerContainer}>
            {filteredCustomerBannerItems.map((customerBannerItem) => {
                const icon = getCustomerBannerMessageIcon(customerBannerItem.variant);
                return (
                    <Alert
                        key={customerBannerItem.id}
                        tone={TONE[customerBannerItem.variant]}
                        live="polite"
                        className={styles.customerBannerMessage}
                        icon={<SvgIcon icon={icon} color="currentColor" />}
                        title={customerBannerItem.title[currentLanguageKey]}
                        onDismiss={() => handleCloseBanner(customerBannerItem.id)}>
                        {customerBannerItem.content[currentLanguageKey]}
                    </Alert>
                );
            })}
        </div>
    );
}

export default CustomerBanner;
